import * as Effect from "effect/Effect";
import * as Schema from "effect/Schema";

import { EnvironmentId, ProjectId, ThreadId, TrimmedNonEmptyString } from "./baseSchemas";

export const ExecutionEnvironmentPlatformOs = Schema.Literals([
  "darwin",
  "linux",
  "windows",
  "unknown",
]);
export type ExecutionEnvironmentPlatformOs = typeof ExecutionEnvironmentPlatformOs.Type;

export const ExecutionEnvironmentPlatformArch = Schema.Literals(["arm64", "x64", "other"]);
export type ExecutionEnvironmentPlatformArch = typeof ExecutionEnvironmentPlatformArch.Type;

export const ExecutionEnvironmentPlatform = Schema.Struct({
  os: ExecutionEnvironmentPlatformOs,
  arch: ExecutionEnvironmentPlatformArch,
});
export type ExecutionEnvironmentPlatform = typeof ExecutionEnvironmentPlatform.Type;

export const ExecutionEnvironmentRuntimeTopology = Schema.Literals([
  "local-web",
  "desktop-local",
  "cloud-shell",
  "cmmd-control-plane",
  "cmmd-cloud-runtime",
]);
export type ExecutionEnvironmentRuntimeTopology = typeof ExecutionEnvironmentRuntimeTopology.Type;

export const ExecutionEnvironmentRuntimeCapabilities = Schema.Struct({
  topology: ExecutionEnvironmentRuntimeTopology,
  controlPlane: Schema.Boolean,
  executionPlane: Schema.Boolean,
  workspaceProxy: Schema.Boolean,
});
export type ExecutionEnvironmentRuntimeCapabilities =
  typeof ExecutionEnvironmentRuntimeCapabilities.Type;

export const ExecutionEnvironmentAuthCapabilities = Schema.Struct({
  cmmd: Schema.Boolean,
  ownerBootstrapPromotion: Schema.Boolean,
  bearerBootstrap: Schema.Boolean,
  desktopBootstrap: Schema.Boolean,
  providerCredentialBroker: Schema.Boolean,
  /**
   * Whether this server accepts the CMMD owner assertion presented as
   * `Authorization: Bearer <assertion>`, not only as the cookie the browser
   * carries. True only in Forge Cloud shell mode, where a native client
   * (the mobile app) has nowhere to keep a cookie. `optionalKey` so an older
   * server that predates this field decodes as unsupported, matching the
   * absent-must-not-mean-healthy rule the rest of this struct follows.
   */
  cloudOwnerAssertion: Schema.optionalKey(Schema.Boolean),
});
export type ExecutionEnvironmentAuthCapabilities = typeof ExecutionEnvironmentAuthCapabilities.Type;

export const ExecutionEnvironmentToolCapabilities = Schema.Struct({
  agents: Schema.Boolean,
  browser: Schema.Boolean,
  github: Schema.Boolean,
  terminal: Schema.Boolean,
  providerEgress: Schema.Boolean,
});
export type ExecutionEnvironmentToolCapabilities = typeof ExecutionEnvironmentToolCapabilities.Type;

export const ExecutionEnvironmentProjectCapabilities = Schema.Struct({
  localProjects: Schema.Boolean,
  cloudProjects: Schema.Boolean,
});
export type ExecutionEnvironmentProjectCapabilities =
  typeof ExecutionEnvironmentProjectCapabilities.Type;

export const ExecutionEnvironmentCapabilities = Schema.Struct({
  repositoryIdentity: Schema.Boolean.pipe(Schema.withDecodingDefault(Effect.succeed(false))),
  pullRequests: Schema.optionalKey(Schema.Boolean),
  /** Whether this environment can durably attach a Command pull request identity to a thread. */
  threadPullRequestLinking: Schema.optionalKey(Schema.Boolean),
  // Compatibility flag for descriptors that predate the grouped capability
  // fields. New clients should prefer `runtime` and `projects` below.
  cloudMode: Schema.Boolean.pipe(Schema.withDecodingDefault(Effect.succeed(false))),
  /** Server understands thread.settle and thread.unsettle commands. Absent on
      pre-settlement servers, so clients treat missing as unsupported. */
  threadSettlement: Schema.optionalKey(Schema.Boolean),
  /** Server understands regenerateTitle on thread.meta.update. Clients hide the
      action when this is absent, so a server that regenerates titles but never
      says so is indistinguishable from one that cannot. */
  threadTitleRegeneration: Schema.optionalKey(Schema.Boolean),
  // PostHog public project key + host. Reused from FORGE_POSTHOG_KEY /
  // FORGE_POSTHOG_HOST (server-side telemetry already uses these). Surfaced
  // to the SPA so the posthog-js client can boot and evaluate feature flags.
  // Null when not configured — the SPA falls back to all-flags-off.
  posthogPublicKey: Schema.NullOr(Schema.String).pipe(
    Schema.withDecodingDefault(Effect.succeed(null)),
  ),
  posthogHost: Schema.NullOr(Schema.String).pipe(Schema.withDecodingDefault(Effect.succeed(null))),
  runtime: Schema.optionalKey(ExecutionEnvironmentRuntimeCapabilities),
  auth: Schema.optionalKey(ExecutionEnvironmentAuthCapabilities),
  tools: Schema.optionalKey(ExecutionEnvironmentToolCapabilities),
  projects: Schema.optionalKey(ExecutionEnvironmentProjectCapabilities),
});
export type ExecutionEnvironmentCapabilities = typeof ExecutionEnvironmentCapabilities.Type;

export const ExecutionEnvironmentDescriptor = Schema.Struct({
  environmentId: EnvironmentId,
  label: TrimmedNonEmptyString,
  platform: ExecutionEnvironmentPlatform,
  serverVersion: TrimmedNonEmptyString,
  /**
   * Commit this server was built from, when it can be established.
   *
   * Optional on purpose: absent honestly means "unknown" on a surface that can
   * neither read env nor reach git, which is better than a placeholder that
   * sends someone to diff against the wrong commit. `serverVersion` alone could
   * not answer "is my merge live?" — every build between two releases reports
   * the same version.
   */
  buildCommit: Schema.optional(TrimmedNonEmptyString),
  /**
   * The CMMD device runtime id this server answers to (`runtime_<uuid>`), when
   * it has one.
   *
   * A machine has TWO names. `environmentId` is minted by this server and is
   * what the SPA connects on; the device runtime id is minted once per install
   * by the desktop app (or handed to a cloud runtime as `FORGE_CMMD_RUNTIME_ID`)
   * and is what the runtime inventory, the Run on chip and the saved-environment
   * registry call this machine.
   *
   * Clients used to learn the second name from their own page storage, which
   * only holds it when the page runs inside the desktop shell. Opening the same
   * local server in an ordinary browser gave that page a different, freshly
   * minted id, so a draft aimed at this desktop read as remote and the model
   * picker asked the user to start the workspace it was already talking to
   * (FOR-36439). The server knows this id, so the server reports it.
   *
   * Absent on a dev checkout, on a plain `forge serve`, and on the cloud shell,
   * none of which register a device runtime. Absent also means "unknown" on
   * older servers, so a client must never treat a missing value as a match.
   */
  deviceRuntimeId: Schema.optional(TrimmedNonEmptyString),
  capabilities: ExecutionEnvironmentCapabilities,
});
export type ExecutionEnvironmentDescriptor = typeof ExecutionEnvironmentDescriptor.Type;

export const EnvironmentConnectionState = Schema.Literals([
  "connecting",
  "connected",
  "disconnected",
  "error",
]);
export type EnvironmentConnectionState = typeof EnvironmentConnectionState.Type;

export const RepositoryIdentityLocator = Schema.Struct({
  source: Schema.Literal("git-remote"),
  remoteName: TrimmedNonEmptyString,
  remoteUrl: TrimmedNonEmptyString,
});
export type RepositoryIdentityLocator = typeof RepositoryIdentityLocator.Type;

export const RepositoryIdentity = Schema.Struct({
  canonicalKey: TrimmedNonEmptyString,
  locator: RepositoryIdentityLocator,
  displayName: Schema.optionalKey(TrimmedNonEmptyString),
  provider: Schema.optionalKey(TrimmedNonEmptyString),
  owner: Schema.optionalKey(TrimmedNonEmptyString),
  name: Schema.optionalKey(TrimmedNonEmptyString),
  /**
   * POSIX-style path of the project's `cwd` relative to the git toplevel.
   *
   * - Empty / undefined for projects whose `cwd` IS the toplevel (or for
   *   non-git projects).
   * - Used by sidebar grouping so that two worktrees of the same repo at the
   *   same in-repo path (e.g. `apps/server` × N worktrees) collapse into one
   *   logical project, while different in-repo paths (e.g. `apps/server` vs
   *   `apps/web`) remain distinct projects.
   */
  repositorySubpath: Schema.optionalKey(Schema.String),
});
export type RepositoryIdentity = typeof RepositoryIdentity.Type;

export const ScopedProjectRef = Schema.Struct({
  environmentId: EnvironmentId,
  projectId: ProjectId,
});
export type ScopedProjectRef = typeof ScopedProjectRef.Type;

export const ScopedThreadRef = Schema.Struct({
  environmentId: EnvironmentId,
  threadId: ThreadId,
});
export type ScopedThreadRef = typeof ScopedThreadRef.Type;

export const ScopedThreadSessionRef = Schema.Struct({
  environmentId: EnvironmentId,
  threadId: ThreadId,
});
export type ScopedThreadSessionRef = typeof ScopedThreadSessionRef.Type;
